/* eslint-disable react/prop-types */
import React from 'react';
import { BOOKING_TIME_SELECTOR_MODAL } from '../../components/ClinicDetailPage/Book/components/BookModule/Modal';
import ClinicDetailPage from '../../components/ClinicDetailPage/index';
import { apiGetDispatchable, apiGetJsonBlocking, shouldBypassCache } from '../../core/dapi/index';
import { getActiveProfileFromState, getProfileFromAccountSummary } from '../../core/util/account';
import {
  getLocationSlotsUrl,
  getNearbyLocationsUrl,
  getSolvAndUclMergedLocationUrl,
  getSolvRatingsByLocationsUrl,
  getTelemedLocationUrl,
} from '../../core/dapi/location';
import {
  setActiveLocationWithoutSlots,
  receiveLocationSlots,
  locationSlotsError,
} from '../../actions/location';
import { connect } from 'react-redux';
import {
  bookingTimeSelected,
  receiveUserProfileForNewBooking,
  setLocationId,
  setOrigin,
} from '../../actions/newBooking';
import { isEmpty } from '../../core/util/empty';
import { isServerSide } from '../../core/util/system';
import { ajaxSuccess, hideModal, showModal } from '../../actions/runtime';
import {
  setNearbyBrandLocation,
  setNearbyLocations,
  setNearbyPartnerLocations,
  setNearbyTelemed,
} from '../../ducks/nearbyClinics/index';
import history from '../../core/history/index';
import { symptomsSubmit } from '../../actions/symptoms';
import { isEmptyString } from '../../core/util/string';
import {
  fromArrayToReactSelectObjArray,
  getDefaultReasonForVisitForLocation,
} from '../../core/util/symptoms';
import { getReasonForVisit } from '../../core/util/form';
import { convertSnakeToCamelCase, safeGet } from '../../core/util/object';
import { profileFormSubmission } from '../../actions/profile';
import { setSolvRatings } from '../../ducks/solvRatings';
import { formatLocationCategory } from '../../core/util/location';
import { PEDIATRIC_URGENT_CARE } from '../../constants/category';
import { PATIENT_TYPE_ADULTS, PATIENT_TYPE_KIDS, ORIGIN_REACT_MOBILE_APP } from '../../constants';
import { isEmptyArray } from '../../core/util/array';
import { getRandomInt } from '../../core/util/numeric';
import withAuthentication from '../../core/login/withAuthentication';
import { prepopulateAppointmentDate } from './util';
import { UtmSourceTypes } from '../../constants/dapi/tracking';

export const SUGGESTIONS = 'symptom-suggestions';
const ENABLE_TELEMED_FOR_NON_PARTNERS = false;

const fetchCDPData = async (locationId, dispatch, shouldUseCache = true) => {
  const url = getSolvAndUclMergedLocationUrl(locationId, shouldUseCache);
  const nearbyUrl = getNearbyLocationsUrl({ locationId, shouldUseCache });
  const nearbyBrandUrl = getNearbyLocationsUrl({
    locationId,
    filterByBrand: true,
    shouldUseCache,
  });
  const solvRatingsUrl = getSolvRatingsByLocationsUrl(locationId);
  const locationPromise = apiGetJsonBlocking(url);
  const nearbyPromise = apiGetJsonBlocking(nearbyUrl);
  const nearbyBrandPromise = apiGetJsonBlocking(nearbyBrandUrl);
  const solvRatingsPromise = apiGetJsonBlocking(solvRatingsUrl);

  const json = await locationPromise;

  if (!isEmpty(json.data)) {
    const isSolvPartner = json.data.is_solv_partner;
    dispatch(setActiveLocationWithoutSlots(json.data));

    /*
      Telemed will show up in the CDP because:
      NON-PARTNERS:
      location.state && !location.isSolvPartner =>
        telemedicine module if:
          telemedicine results set within state

      PARTNERS:
      location.groupId && location.isSolvPartner =>
        telemedicine within same group
     */
    const groupIds = safeGet(json.data, [])('groups').map(group => group.group_id);
    const patientType =
      (json.data.is_pediatrics_only && PATIENT_TYPE_KIDS) ||
      (formatLocationCategory(json.data) === PEDIATRIC_URGENT_CARE
        ? PATIENT_TYPE_KIDS
        : PATIENT_TYPE_ADULTS);

    let telemedUrl;
    if (isSolvPartner && !isEmptyArray(groupIds)) {
      telemedUrl = getTelemedLocationUrl({ groupIds, patientType });
    } else if (ENABLE_TELEMED_FOR_NON_PARTNERS && !isSolvPartner) {
      telemedUrl = getTelemedLocationUrl({
        state: json.data.state,
        patientType,
      });
    }

    const telemedPromise = telemedUrl && apiGetJsonBlocking(telemedUrl);
    if (telemedPromise) {
      const telemedLocation = await telemedPromise;
      if (telemedLocation.data && telemedLocation.data.results.length >= 1) {
        const randomIndex = getRandomInt(telemedLocation.data.results.length);
        dispatch(setNearbyTelemed(telemedLocation.data.results[randomIndex]));
      }
    }

    // brands and reviews are needed regardless of solvPartner status
    const brands = await nearbyBrandPromise;
    const solvRatings = await solvRatingsPromise;

    dispatch(setNearbyBrandLocation(brands.data));
    dispatch(setSolvRatings(solvRatings.data));

    // Fetch nearby if the cdp is that of a non-partner
    if (!isSolvPartner) {
      const nearby = await nearbyPromise;
      if (!isEmpty(nearby.data) && !isEmpty(nearby.data.results)) {
        dispatch(setNearbyLocations(nearby.data.results));
      }
    }
  }

  return json.data; // Return location data.
};

const mapStateToProps = (state, ownProps) => {
  const userProfile = !isEmpty(ownProps.userProfileId)
    ? getProfileFromAccountSummary(state.account.summary, ownProps.userProfileId)
    : getActiveProfileFromState(state);

  let suggestions = [];
  if (
    state.runtime.ajax[SUGGESTIONS] &&
    state.runtime.ajax[SUGGESTIONS].result &&
    state.runtime.ajax[SUGGESTIONS].result.results
  ) {
    suggestions = fromArrayToReactSelectObjArray(state.runtime.ajax[SUGGESTIONS].result.results);
  }

  return {
    accountSummary: state.account.summary,
    distanceMatrix: state.distanceMatrix,
    location: state.location,
    login: state.login,
    newBooking: state.newBooking,
    searchResults: state.searchResults,
    nearbyLocations: state.nearbyLocations,
    userProfile,
    viewport: state.runtime.viewport,
    modalState: state.runtime.modal || {},
    reasonForVisit: getReasonForVisit(state.newBooking.booking.reasonForVisit),
    suggestions,
    persistentStateLoaded: state.runtime ? state.runtime.persistentStateLoaded : false,
    sessionTrackingProperties: state.sessionTrackingProperties,
    trackingPropertiesReferrer: safeGet(state.sessionTrackingProperties)('referrer'),
  };
};

export const mapDispatchToProps = (dispatch, ownProps) => ({
  fetchLocationData: async locationId => await fetchCDPData(locationId, dispatch),

  fetchLocationSlotsData: locationId => {
    dispatch(
      apiGetDispatchable(getLocationSlotsUrl(locationId), receiveLocationSlots, locationSlotsError)
    );
  },

  handleRequestAppointmentClick(appointmentTime, login, symptoms, location) {
    dispatch(bookingTimeSelected(appointmentTime));
    if (symptoms !== getDefaultReasonForVisitForLocation(location)) {
      dispatch(symptomsSubmit({ symptoms }));
    }

    if (ownProps.isLoggedIn) {
      history.push('/confirm');
    } else {
      history.push('/profile');
    }

    dispatch(hideModal(BOOKING_TIME_SELECTOR_MODAL));
  },

  setBookingProfile: (accountSummary, userProfileId) => {
    const userProfile = getProfileFromAccountSummary(accountSummary, userProfileId);
    dispatch(receiveUserProfileForNewBooking(userProfile));
    dispatch(
      profileFormSubmission({
        ...convertSnakeToCamelCase(userProfile),
        phone: accountSummary.phone,
        email: accountSummary.email,
      })
    );
  },

  setUserProfile: userProfile => dispatch(receiveUserProfileForNewBooking(userProfile)),

  showModal: modalId => dispatch(showModal(modalId)),
  hideModal: modalId => dispatch(hideModal(modalId)),

  fetchPartnerClinics: ({ latLong: { latitude, longitude } }) =>
    dispatch(
      apiGetDispatchable(
        getNearbyLocationsUrl({
          lat: latitude,
          lng: longitude,
          isSolvPartner: true,
        }),
        setNearbyPartnerLocations
      )
    ),

  clearSuggestions: () => {
    dispatch(ajaxSuccess(SUGGESTIONS)([]));
  },

  toggleEmergencyModal: value => {
    if (value) {
      dispatch(showModal('emergency'));
    } else {
      dispatch(hideModal('emergency'));
    }
  },

  toggleTelemedModal: value => {
    if (value) {
      dispatch(showModal('telemed'));
    } else {
      dispatch(hideModal('telemed'));
    }
  },
});

const ConnectedClinicDetailPage = withAuthentication(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(ClinicDetailPage)
);

const accountBookForUserRoute = (context, params) => ({
  chunks: ['cdp'],
  component: <ConnectedClinicDetailPage userProfileId={params.userProfileId} />,
});

const cdpRoute = async (context, params) => {
  const locationId = params.id;
  const shouldUseCache = !shouldBypassCache(context);

  const utmSource = safeGet(context.query)('utm_source');
  if (utmSource === UtmSourceTypes.GMB) {
    return {
      redirect: `/book-online/${locationId}`,
    };
  }

  context.store.dispatch(setOrigin(ORIGIN_REACT_MOBILE_APP));
  context.store.dispatch(setLocationId(locationId));

  prepopulateAppointmentDate(context);

  if (isServerSide()) {
    const location = await fetchCDPData(locationId, context.store.dispatch, shouldUseCache);

    const state = context.store.getState();
    const referrer = safeGet(state.sessionTrackingProperties)('referrer');
    if (!isEmptyString(referrer)) {
      const isReferrerKnownPartnerDomain = location.groups.some(group =>
        group.domains.some(domain => referrer.indexOf(domain) > -1)
      );
      if (isReferrerKnownPartnerDomain) {
        return {
          redirect: `/book-online/${locationId}`,
        };
      }
    }
  }

  return {
    chunks: ['cdp'],
    component: <ConnectedClinicDetailPage requestId={locationId} />,
    accountWrapper: {
      loginRequired: false,
      nonBlocking: true,
    },
  };
};

export { accountBookForUserRoute };
export default cdpRoute;
